import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Heading,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  Grid,
  GridItem,
  Container,
  VStack,
} from "@chakra-ui/react";
import { useAuth } from "../../Contexts/AuthContext";
import { getContracts, getContractHistory } from "../../Services/api";
import ContractCard from "../../Components/shared/ContractCard";

const BillingPage = () => {
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState({});
  const [error, setError] = useState("");
  const { user } = useAuth();

  // جلب العقود الأساسية فقط
  useEffect(() => {
    if (!user?.CustomerId) {
      setIsLoading(false);
      setError("User information is not available.");
      return;
    }

    setIsLoading(true);
    getContracts(user.CustomerId)
      .then((response) => {
        const contractsData = response.data?.data?.results || [];
        const contractsWithState = contractsData.map((contract) => ({
          ...contract,
          historyLoaded: false,
          historyData: null,
        }));
        setContracts(contractsWithState);
      })
      .catch((err) => {
        setError(err.message || "Failed to fetch contracts.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [user]);

  const loadContractHistory = useCallback(async (contractId, index) => {
    if (!contractId) return;

    setLoadingHistory((prev) => ({ ...prev, [contractId]: true }));

    try {
      const response = await getContractHistory(contractId);

      setContracts((prev) =>
        prev.map((contract, i) =>
          i === index
            ? {
                ...contract,
                historyLoaded: true,
                historyData: response.data,
              }
            : contract,
        ),
      );
    } catch (err) {
      console.error(`Failed to load history for contract ${contractId}:`, err);
      setError(`Failed to load history for contract ${contractId}`);
    } finally {
      setLoadingHistory((prev) => ({ ...prev, [contractId]: false }));
    }
  }, []);

  if (isLoading) {
    return (
      <Box
        textAlign="center"
        minH="60vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={4}>
          <Spinner size="xl" thickness="3px" color="purple.500" />
          <Text fontSize="lg" color="gray.600">
            Loading your automation packages...
          </Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" mt={8}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      bg="gray.50"
      height="calc(100dvh - 3rem)"
      overflowY={"auto"}
    >
      <Container maxW="container.xl">
        {/* Automation Packages Grid */}
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          }}
          gap={0.5}
        >
          {contracts.length > 0 ? (
            contracts.map((contract, index) => (
              <GridItem key={contract.ContractId}>
                <ContractCard
                  contract={contract}
                  index={index}
                  onLoadHistory={() =>
                    loadContractHistory(contract.ContractId, index)
                  }
                  isLoadingHistory={loadingHistory[contract.ContractId]}
                />
              </GridItem>
            ))
          ) : (
            <GridItem colSpan={{ base: 1, md: 2, lg: 3 }}>
              <Box
                textAlign="center"
                p={12}
                borderRadius="xl"
                bg="white"
                border="2px dashed"
                borderColor="gray.200"
              >
                <Text fontSize="xl" fontWeight="bold" mb={3} color="gray.700">
                  No Automation Packages Found
                </Text>
                <Text color="gray.500" maxW="md" mx="auto">
                  Your automation packages will appear here once they're
                  activated.
                </Text>
              </Box>
            </GridItem>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default BillingPage;
